import { CreateResult, GetListResult, GetOneResult } from "react-admin";
import HttpClient from "../HttpClient";
import AreaResponse from "../interfaces/Area/AreaResponse";
import CreateArea from "../interfaces/Area/CreateArea";
import ConstructSequelizeFilters from "../utils/ConstructSequelizeFilters";
import { ReferenceModelNameEnums } from "../enums/ReferenceModelNameEnums";
import ServiceResponse from "../interfaces/Service/ServiceResponse";
import { SlcServiceCookMappingEnum } from "../enums/SlcServiceCookMappingEnum";

export default class AreaService {
  async fetchAllAreas(
    limit: number,
    offset: number,
    filters: any,
    sort: {}
  ): Promise<GetListResult<AreaResponse>> {
    if(filters.cityId){
      if(!Array.isArray(filters.cityId)){
        let cities =[];
        cities.push(filters.cityId);
        filters.cityId = cities;
      }
    }
    filters = ConstructSequelizeFilters(filters, ["name"],[],[],[],['cityId']);
    let data = await HttpClient.get("/centaurus/houses/areas", {
      params: {
        limit,
        offset,
        filters,
        sort,
      },
    });
    return {
      data: !!data && !!data?.data ? data?.data?.data : [],
      total: !!data && !!data?.data ? data?.data?.count : 0,
    };
  }

  async fetchOne(areaId: number): Promise<GetOneResult<AreaResponse>> {
    const area = await HttpClient.get(`/centaurus/houses/areas/${areaId}`);
    return area;
  }

  async getSlcCookServiceMappingForVisit(payload:any):Promise<any>{
    const params: any = {
      serviceId: payload.serviceId,
      referenceModelName: 'AREA',
      referenceId: payload.referenceId,
      time: payload.time,
      date: payload.date,
      houseId: payload.houseId,
    };
    // Slot bookings take precedence over people bookings
    if (payload.slotSize != null) {
      params.slotSize = payload.slotSize;
    } else if (payload.totalPeople != null) {
      params.totalPeople = payload.totalPeople;
    }
    const res= await HttpClient.get(`/houses/get-cook-slc-mapping-by-area-name`, { params });
    return res;
  }

  async getAreasByCityId(cityId:number):Promise<any>{

    const areas= await HttpClient.get(`/centaurus/newareas/city/${cityId}`);
    return areas;
    
  }

  async getMany(ids: Array<number>): Promise<GetListResult<AreaResponse>> {
    const data = [];

    for (const id of ids) {
      const res = await this.fetchOne(id);
      data.push(res?.data);
    }

    return {
      data: data,
      total: data?.length,
    };
  }

  async create(payload: CreateArea): Promise<CreateResult<AreaResponse>> {
    const isServiceable = payload.isServiceable === "true" ? true : false;
    const res = await HttpClient.post(
      `/centaurus/houses/${payload.cityId}/area`,
      { name: payload.name, isServiceable: isServiceable }
    );
    return {
      data: res?.data,
    };
  }

  async markAsServiceable(areaId: number): Promise<AreaResponse> {
    const res = await HttpClient.patch(
      `/centaurus/areas/${areaId}/mark-as-serviceable`
    );
    return res?.data;
  }

  async markAsInstantChefitServiceable(areaId: number, payload: any): Promise<AreaResponse> {
    const res = await HttpClient.patch(
      `/centaurus/areas/${areaId}/mark-instant-chefit`,
        payload,
    );
    return res?.data;
  }

  async markPayAfterService(areaId: number, payload: any): Promise<AreaResponse> {
    const res = await HttpClient.patch(
      `/centaurus/areas/${areaId}/mark-pay-after-service`,
        payload,
    );
    return res?.data;
  }

  async markAsUnServiceable(areaId: number): Promise<AreaResponse> {
    const res = await HttpClient.patch(
      `/centaurus/areas/${areaId}/mark-as-unserviceable`
    );
    return res?.data;
  }

  async getAllServiceOfferings(payload: {
    referenceId: number;
    referenceModelName: ReferenceModelNameEnums;
  }): Promise<ServiceResponse[]> {
    const res = await HttpClient.get(`/houses/get-offering-by-slc`, {
      params: {
        ...payload,
      },
    });
    return res?.data;
  }

  async getAllServices(): Promise<ServiceResponse[]> {
    const res = await HttpClient.get(`/houses/get-service-offerings`);
    return res?.data;
  }

  async createServiceForSlc(payload: {
    referenceId: number;
    referenceModelName: ReferenceModelNameEnums;
    serviceId: number;
    isServing: boolean;
  }): Promise<ServiceResponse> {
    const res = await HttpClient.post(
      `/houses/create-slc-service-mapping`,
      payload
    );
    return res?.data;
  }

  async changeCookServeForSlc(payload: {
    referenceId: number;
    referenceModelName: ReferenceModelNameEnums;
    serviceId: number;
    isCookServed: boolean;
  }): Promise<ServiceResponse> {
    const res = await HttpClient.patch(`/centaurus/houses/locality/set-cook-served-by-area`, payload);
    return res?.data;
  }

  async updateServiceForSlc(payload: {
    referenceId: number;
    referenceModelName: ReferenceModelNameEnums;
    serviceId: number;
    isServing: boolean;
  }): Promise<ServiceResponse> {
    const res = await HttpClient.patch(
      `/houses/create-slc-service-mapping/${payload?.serviceId}`,
      payload
    );
    return res?.data;
  }

  async getAllSclCooks(params: any): Promise<ServiceResponse> {
    const res = await HttpClient.get(
      `/centaurus/slc-cook-mapping/by-area/${params.serviceId}/${params.areaId}`,
      {
        params: {
          ...params?.filters,
        },
      }
    );
    return res?.data;
  }

  async addCooksToSlc(payload: {
    referenceId: number;
    referenceModelName: string;
    serviceId: number;
    cookId: number;
    mappingType: SlcServiceCookMappingEnum;
  }): Promise<ServiceResponse> {
    const res = await HttpClient.post(
      `/centaurus/slc-cook-mapping/by-area/add`,
      payload
    );
    return res?.data;
  }

  async removeSlcCooks(payload: any): Promise<ServiceResponse> {
    const res = await HttpClient.post(
      `/centaurus/slc-cook-mapping/by-area/delete`,
      payload
    );
    return res?.data;
  }

  async deactivateSlcCookMapping(id: number): Promise<ServiceResponse> {
    const res = await HttpClient.patch(
      `/centaurus/slc-cook-mapping/${id}/deactivate`
    );
    return res?.data;
  }

  async activateSlcCookMapping(id: number): Promise<ServiceResponse> {
    const res = await HttpClient.patch(
      `/centaurus/slc-cook-mapping/${id}/activate`
    );
    return res?.data;
  }

  async editAreaLatLong(id: number, payload: {coordinates: {lat: number, long: number}}){
    const data = await HttpClient.patch(`/centaurus/areas/${id}/update-lat-log`, payload)
    return data?.data
  }

  async getBookingSlotThreshold(filters: any, limit: number, offset: number) {
    const data = await HttpClient.get(`/centaurus/booking-slot-threshold/all?limit=${limit}&offset=${offset}&filters=${filters}`);
    return data?.data;
  }

  async createSlotThreshold(payload: any) {
    const data = await HttpClient.patch(`centaurus/booking-slot-threshold`, payload);
    return data?.data;
  }

  async updateBookByDishMultiplier(areaId: number, bookByDishMultiplier: number): Promise<any> {
    const res = await HttpClient.patch(`/centaurus/areas/${areaId}/book-by-dish-multiplier`, { bookByDishMultiplier });
    return res?.data;
  }
}
